import { ScrollArea as ScrollAreaPrimitive } from 'radix-ui';
import type { ComponentProps, Ref } from 'react';

import { cn } from './cn';

type ViewportProps = Omit<ComponentProps<typeof ScrollAreaPrimitive.Viewport>, 'children'>;

/**
 * Rolagem com a barra fina do Lume (shadcn) no lugar da nativa: aparece no hover e ao rolar. O
 * `viewport` é o elemento que rola (ref, papel e foco vão nele).
 */
export function ScrollArea({
  className,
  children,
  viewport,
  viewportRef,
  axes = 'y',
  ...props
}: ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  readonly viewport?: ViewportProps;
  readonly viewportRef?: Ref<HTMLDivElement>;
  readonly axes?: 'y' | 'both';
}) {
  return (
    <ScrollAreaPrimitive.Root data-slot="scroll-area" type="hover" className={cn('relative overflow-hidden', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        {...viewport}
        // O Radix embrulha o conteúdo em `display: table`, que quebra `truncate` e larguras em %.
        className={cn('size-full rounded-[inherit] outline-none [&>div]:!block', viewport?.className)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar orientation="vertical" />
      {axes === 'both' ? <ScrollBar orientation="horizontal" /> : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

export function ScrollBar({ className, orientation = 'vertical', ...props }: ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'z-40 flex touch-none p-px transition-opacity duration-(--lume-dur-fast) select-none data-[state=hidden]:opacity-0',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb data-slot="scroll-area-thumb" className="relative flex-1 rounded-full bg-structural hover:bg-muted-foreground/50" />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}
